import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api from '../services/api';

const AppContext = createContext();

// ── App-wide settings (persisted in localStorage) ─────────────────────────
const SETTINGS_KEY = 'appSettings';
const DEFAULT_SETTINGS = {
  darkMode: false,
  language: 'en',      // 'en' | 'hi'
  timezone: 'Asia/Kolkata',
  notifications: true,
  emailAlerts: false,
  smsAlerts: true,
  appointmentReminder: true,
  twoFactor: false,
};

const loadSettings = () => {
  try { return { ...DEFAULT_SETTINGS, ...JSON.parse(localStorage.getItem(SETTINGS_KEY)) }; }
  catch { return { ...DEFAULT_SETTINGS }; }
};

const applyTheme = (darkMode) => {
  document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
};

// Apply saved theme synchronously before first render to avoid flash
applyTheme(loadSettings().darkMode);

// Simple event emitter for cross-component refresh signaling
const refreshListeners = {};
export const onRefresh = (key, fn) => {
  if (!refreshListeners[key]) refreshListeners[key] = new Set();
  refreshListeners[key].add(fn);
  return () => refreshListeners[key].delete(fn);
};
export const triggerRefresh = (key) => {
  if (refreshListeners[key]) refreshListeners[key].forEach(fn => fn());
  // Also trigger 'all' listeners
  if (key !== 'all' && refreshListeners['all']) refreshListeners['all'].forEach(fn => fn());
};

export const AppProvider = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')) || null; } catch { return null; }
  });
  const [badges, setBadges] = useState({
    appointments: 0, prescriptions: 0, labTests: 0,
    billing: 0, telemedicine: 0, messages: 0, notifications: 0,
  });
  const [settings, setSettings] = useState(loadSettings);

  // Update a single setting, persist to localStorage, and apply side-effects
  const updateSetting = useCallback((key, value) => {
    setSettings(prev => {
      const next = { ...prev, [key]: value };
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
      if (key === 'darkMode') applyTheme(value);
      return next;
    });
  }, []);

  // Date/time formatter that respects the user's language and timezone settings
  const formatDateTime = useCallback((date, opts = {}) => {
    if (!date) return '—';
    const d = typeof date === 'string' ? new Date(date) : date;
    const locale = settings.language === 'hi' ? 'hi-IN' : 'en-IN';
    return d.toLocaleString(locale, { timeZone: settings.timezone, ...opts });
  }, [settings.language, settings.timezone]);

  const formatDate = useCallback((date) => {
    if (!date) return '—';
    const d = typeof date === 'string' ? new Date(date) : date;
    const locale = settings.language === 'hi' ? 'hi-IN' : 'en-IN';
    return d.toLocaleDateString(locale, { timeZone: settings.timezone });
  }, [settings.language, settings.timezone]);

  const openSidebar = useCallback(() => setSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  const showToast = useCallback((message, type = 'default') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const updateBadges = useCallback((newBadges) => {
    setBadges(prev => ({ ...prev, ...newBadges }));
  }, []);

  const login = useCallback(async (form, mode = 'login') => {
    const endpoint = mode === 'login' ? '/auth/login' : '/auth/signup';
    const res = await api.post(endpoint, form);
    if (mode === 'login') {
      const { token, user: userData } = res.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    }
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setBadges({ appointments: 0, prescriptions: 0, labTests: 0, billing: 0, telemedicine: 0, messages: 0, notifications: 0 });
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      const userData = res.data.user;
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    } catch {}
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && user) refreshUser();
  }, []); // eslint-disable-line

  // Poll unread notification count — updates the bell badge in the Header
  const pollNotifications = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const res = await api.get('/notifications');
      setBadges(prev => ({ ...prev, notifications: res.data?.unread || 0 }));
    } catch { /* ignore — user may not be logged in yet */ }
  }, []);

  // Poll only while the tab is actually visible — a background/minimized tab
  // contributes zero request load instead of polling forever unseen. At scale
  // this is the difference between "every open tab hits the API" and
  // "every tab the user is actually looking at hits the API".
  useEffect(() => {
    if (!user) return;
    const tick = () => { if (document.visibilityState === 'visible') pollNotifications(); };
    tick(); // immediate fetch on login / page load
    const interval = setInterval(tick, 20000); // was 3s — 3s per open tab does not scale
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [user, pollNotifications]);

  // Global auto-poll — triggers refresh events for all active modules.
  // Same visibility gating as notifications above.
  useEffect(() => {
    if (!user) return;
    const tick = () => { if (document.visibilityState === 'visible') triggerRefresh('all'); };
    const interval = setInterval(tick, 30000); // was 3s — matches the documented "30s global poll" design
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [user]);

  return (
    <AppContext.Provider value={{
      sidebarOpen, openSidebar, closeSidebar,
      toast, showToast,
      badges, updateBadges,
      user, login, logout, refreshUser,
      triggerRefresh,
      settings, updateSetting,
      formatDateTime, formatDate,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};

// Hook for refresh: re-runs fetchFn only when triggerRefresh(key) is called (no automatic polling)
export const useAutoRefresh = (fetchFn, key = 'all') => {
  const fetchRef = useRef(fetchFn);
  fetchRef.current = fetchFn;

  useEffect(() => {
    const unsub = onRefresh(key, () => fetchRef.current());
    return unsub;
  }, [key]);
};

// Returns a function that prepends the logged-in user's role base path
// rp('/appointments') → '/patient/appointments' or '/doctor/appointments' etc.
export const useRolePath = () => {
  const { user } = useApp();
  const base = user?.role === 'Patient' ? '/patient'
             : user?.role === 'Doctor'  ? '/doctor'
             : user?.role === 'Admin'   ? '/admin'
             : '';
  return useCallback((path = '/') => {
    if (!path || path === '/') return base || '/';
    const clean = path.startsWith('/') ? path : `/${path}`;
    return `${base}${clean}`;
  }, [base]);
};

export default AppContext;
